/**
 * Local offline English/Hinglish-to-Marathi phonetic transliteration utility.
 * Resolves English words to Devanagari script for bilingual rendering.
 */

// A high-accuracy dictionary mapping common names, locations, and roles to Marathi Devanagari.
const TRANSLITERATION_DICT: Record<string, string> = {
  // Names
  pruthvi: 'पृथ्वी',
  prithvi: 'पृथ्वी',
  pravin: 'प्रविण',
  pravine: 'प्रविण',
  tejaswi: 'तेजस्वी',
  tejaswin: 'तेजस्वी',
  tejasvi: 'तेजस्वी',
  aarav: 'आरव',
  mira: 'मीरा',
  meera: 'मीरा',
  rahul: 'राहुल',
  priya: 'प्रिया',
  amit: 'अमित',
  sanjay: 'संजय',
  neelam: 'नीलम',
  kavita: 'कविता',
  sharma: 'शर्मा',
  mehta: 'मेहता',
  erande: 'एरंडे',
  khule: 'खुले',
  shobha: 'शोभा',
  sachita: 'सचिता',
  gorakhnath: 'गोरक्षनाथ',
  gorakshanath: 'गोरक्षनाथ',
  martand: 'मार्तंड',
  lahanbhau: 'लहानभाऊ',
  rakhamaji: 'रखमाजी',
  patil: 'पाटील',
  pat: 'पा.',

  // Locations
  sangamner: 'संगमनेर',
  kolhewadi: 'कोल्हेवाडी',
  zole: 'झोळे',
  rapo: 'रा.',
  ta: 'ता.',
  tal: 'ता.',
  dist: 'जि.',
  jila: 'जि.',
  anagar: 'अ.नगर',
  ahmednagar: 'अहमदनगर',
  ahilyanagar: 'अहिल्यानगर',
  rajapur: 'राजापूर',
  mumbai: 'मुंबई',
  pune: 'पुणे',
  dhole: 'ढोले',
  lawns: 'लॉन्स',
  road: 'रोड',

  // Wedding Roles & Greetings
  groom: 'वर',
  bride: 'वधू',
  father: 'वडील',
  mother: 'आई',
  parents: 'पालक',
  family: 'परिवार',
  shree: 'श्री',
  shri: 'श्री',
  ganeshay: 'गणेशाय',
  namah: 'नमः',
  snvivi: 'स.न.वि.वि.',
  snviv: 'स.न.वि.वि.',
  chi: 'चि.',
  chisoukan: 'चि.सौ.कां.',
  sou: 'सौ.',
  shrimati: 'श्रीमती',
  invited: 'निमंत्रक',
  inviter: 'निमंत्रक',
  hosts: 'निमंत्रक',
  attending: 'उपस्थित',
  rsvp: 'आर.एस.व्ही.पी.',

  // Events & Days
  haldi: 'हळद समारंभ',
  wedding: 'शुभविवाह',
  marriage: 'शुभविवाह सोहळा',
  reception: 'स्वागतसमारंभ',
  mehendi: 'मेहंदी सोहळा',
  sangeet: 'संगीत सोहळा',
  ceremony: 'सोहळा',
  event: 'कार्यक्रम',
  events: 'कार्यक्रम',
  venue: 'स्थळ',
  date: 'दिनांक',
  time: 'वेळ',
  day: 'वार',

  // Months
  january: 'जानेवारी',
  february: 'फेब्रुवारी',
  march: 'मार्च',
  april: 'एप्रिल',
  may: 'मे',
  june: 'जून',
  july: 'जुलै',
  august: 'ऑगस्ट',
  september: 'सप्टेंबर',
  october: 'ऑक्टोबर',
  november: 'नोव्हेंबर',
  december: 'डिसेंबर',

  // Weekdays
  sunday: 'रविवार',
  monday: 'सोमवार',
  tuesday: 'मंगळवार',
  wednesday: 'बुधवार',
  thursday: 'गुरुवार',
  friday: 'शुक्रवार',
  saturday: 'शनिवार',

  // Conjunctions & Prepositions
  and: 'व',
  of: 'येथील',
  with: 'सह',
  to: 'प्रती',
  sonof: 'यांचे चिरंजीव',
  daughterof: 'यांची कन्या',
  fatherof: 'यांचे वडील',
  motherof: 'यांची आई',
  wifeof: 'यांची पत्नी',
  's/o': 'यांचे चिरंजीव',
  'd/o': 'यांची कन्या',
  'f/o': 'यांचे वडील',
  'm/o': 'यांची आई',
  'w/o': 'यांची पत्नी',
  pm: 'वाजता',
  am: 'वाजता',
  at: 'येथे',
};

// Consonant cluster mappings for rule-based transliteration
const CONSONANT_CLUSTERS: Array<{ key: string; val: string }> = [
  { key: 'dny', val: 'ज्ञ' },
  { key: 'ksh', val: 'क्ष' },
  { key: 'chh', val: 'छ' },
  { key: 'shr', val: 'श्र' },
  { key: 'dhy', val: 'ध्य' },
  { key: 'kh', val: 'ख' },
  { key: 'gh', val: 'घ' },
  { key: 'ch', val: 'च' },
  { key: 'jh', val: 'झ' },
  { key: 'th', val: 'थ' },
  { key: 'dh', val: 'ध' },
  { key: 'ph', val: 'फ' },
  { key: 'bh', val: 'भ' },
  { key: 'sh', val: 'श' },
  { key: 'shh', val: 'ष' },
  { key: 'gy', val: 'ज्ञ' },
  { key: 'nh', val: 'न्ह' },
  { key: 'mh', val: 'म्ह' },
  { key: 'lh', val: 'ल्ह' },
  { key: 'pr', val: 'प्र' },
  { key: 'kr', val: 'क्र' },
  { key: 'tr', val: 'त्र' },
  { key: 'gr', val: 'ग्र' },
  { key: 'br', val: 'ब्र' },
  { key: 'dr', val: 'द्र' },
  { key: 'ry', val: 'र्य' },
];

const SINGLE_CONSONANTS: Record<string, string> = {
  k: 'क',
  g: 'ग',
  c: 'च',
  j: 'ज',
  t: 'त', // mapped to त as default, Patil/etc. are handled by dict
  d: 'द',
  n: 'न',
  p: 'प',
  b: 'ब',
  m: 'म',
  y: 'य',
  r: 'र',
  l: 'ल',
  v: 'व',
  w: 'व',
  s: 'स',
  h: 'ह',
  z: 'झ',
  x: 'क्ष',
  f: 'फ',
  q: 'क',
};

const VOWEL_MATRAS: Record<string, string> = {
  aa: 'ा',
  ee: 'ी',
  oo: 'ू',
  ai: 'ै',
  au: 'ौ',
  ou: 'ौ',
  a: 'ा', // default vowel sign is empty or 'ा' at end
  i: 'ि',
  u: 'ु',
  e: 'े',
  o: 'ो',
};

const INDEPENDENT_VOWELS: Record<string, string> = {
  a: 'अ',
  aa: 'आ',
  i: 'इ',
  ee: 'ई',
  u: 'उ',
  oo: 'ऊ',
  e: 'ए',
  ai: 'ऐ',
  o: 'ओ',
  au: 'औ',
  ou: 'औ',
};

/**
 * Phonetically transliterates a single English word to Marathi Devanagari script.
 */
export function transliterateWord(word: string): string {
  if (!word) return '';

  // Clean the word of surrounding punctuation for lookup
  const cleanWord = word.toLowerCase().replace(/^[.,/#!$%^&*;:{}=\-_`~()]+|[.,/#!$%^&*;:{}=\-_`~()]+$/g, '');
  if (!cleanWord) return word;

  if (TRANSLITERATION_DICT[cleanWord]) {
    // Preserve any punctuation that was stripped
    const prefix = word.match(/^[.,/#!$%^&*;:{}=\-_`~()]+/)?.[0] || '';
    const suffix = word.match(/[.,/#!$%^&*;:{}=\-_`~()]+$/)?.[0] || '';
    return prefix + TRANSLITERATION_DICT[cleanWord] + suffix;
  }

  // Fallback to rule-based parser
  let devanagari = '';
  let i = 0;
  const len = cleanWord.length;

  let lastCharWasConsonant = false;

  while (i < len) {
    let matched = false;

    // 1. Try to match consonant clusters first
    for (const cluster of CONSONANT_CLUSTERS) {
      if (cleanWord.substring(i, i + cluster.key.length) === cluster.key) {
        if (lastCharWasConsonant) {
          devanagari += '्'; // Append halant to join preceding consonant
        }
        devanagari += cluster.val;
        i += cluster.key.length;
        matched = true;
        lastCharWasConsonant = true;
        break;
      }
    }
    if (matched) continue;

    // 2. Try to match single consonants
    const char = cleanWord[i];
    if (SINGLE_CONSONANTS[char]) {
      if (lastCharWasConsonant) {
        devanagari += '्'; // Join conjoined consonants
      }
      devanagari += SINGLE_CONSONANTS[char];
      i += 1;
      lastCharWasConsonant = true;
      continue;
    }

    // 3. Try to match vowels
    let vowelMatch = '';
    if (i + 1 < len && VOWEL_MATRAS[cleanWord.substring(i, i + 2)]) {
      vowelMatch = cleanWord.substring(i, i + 2);
    } else if (VOWEL_MATRAS[char]) {
      vowelMatch = char;
    }

    if (vowelMatch) {
      if (lastCharWasConsonant) {
        // Apply matra/vowel sign to previous consonant
        const matra = VOWEL_MATRAS[vowelMatch];
        if (vowelMatch !== 'a') {
          devanagari += matra;
        }
        lastCharWasConsonant = false;
      } else {
        // Independent vowel letter
        devanagari += INDEPENDENT_VOWELS[vowelMatch] || vowelMatch;
      }
      i += vowelMatch.length;
      continue;
    }

    // Unmapped character (numbers, symbols, non-ASCII)
    devanagari += char;
    i += 1;
    lastCharWasConsonant = false;
  }

  // Restore punctuation
  const prefix = word.match(/^[.,/#!$%^&*;:{}=\-_`~()]+/)?.[0] || '';
  const suffix = word.match(/[.,/#!$%^&*;:{}=\-_`~()]+$/)?.[0] || '';
  return prefix + devanagari + suffix;
}

/**
 * Phonetically transliterates a full sentence or paragraph of English text to Marathi Devanagari.
 */
export function transliterateToMarathi(text: string): string {
  if (!text) return '';
  
  // Pre-replace common relation phrases and titles case-insensitively
  let processedText = text
    .replace(/\bson\s+of\b/gi, 'sonof')
    .replace(/\bdaughter\s+of\b/gi, 'daughterof')
    .replace(/\bfather\s+of\b/gi, 'fatherof')
    .replace(/\bmother\s+of\b/gi, 'motherof')
    .replace(/\bwife\s+of\b/gi, 'wifeof')
    .replace(/\bs\s*\/\s*o\b/gi, 's/o')
    .replace(/\bd\s*\/\s*o\b/gi, 'd/o')
    .replace(/\bf\s*\/\s*o\b/gi, 'f/o')
    .replace(/\bm\s*\/\s*o\b/gi, 'm/o')
    .replace(/\bw\s*\/\s*o\b/gi, 'w/o')
    .replace(/\bmr\b\.?/gi, 'shri')
    .replace(/\bmrs\b\.?/gi, 'sou')
    .replace(/\bms\b\.?/gi, 'sou');

  // Split text by whitespace, preserving tabs and newlines
  const wordsAndDelimiters = processedText.split(/(\s+)/);
  return wordsAndDelimiters
    .map((token) => {
      // If token is whitespace, preserve it as-is
      if (/^\s+$/.test(token)) return token;
      return transliterateWord(token);
    })
    .join('');
}
